import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslations } from 'src/contexts/TranslationContext';
import { NewsListItems, fetchArchiveNewsByDate } from './NewsAPIUtil';
import RobotsNoIndex from 'src/components/seo/RobotsNoIndex';

// Cache interface
// interface NewsCache {
//     [key: string]: {
//         news: NewsListItems[];
//         timestamp: number;
//         type: string;
//         subtype?: string;
//         minTrendingScore?: number;
//     }
// }

// Cache expiration time (30 minutes)
// const CACHE_EXPIRATION = 30 * 60 * 1000;

// Global news cache
// const NEWS_CACHE: NewsCache = {};

interface ArchiveSubtypeProps {
    lang?: string;
}

const ArchiveSubtype: React.FC<ArchiveSubtypeProps> = () => {
    const { date } = useParams<{ date: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const { language, getUrlLanguagePrefix, translations: t } = useTranslations();
    const [news, setNews] = useState<NewsListItems[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Get type, subtype, and minTrendingScore from query parameters
    const searchParams = new URLSearchParams(location.search);
    const type = searchParams.has('type') ? searchParams.get('type') : undefined;  // Set to undefined instead of null
    const subtype = searchParams.get('subtype') || undefined;
    const minTrendingScoreParam = searchParams.get('minTrendingScore');
    const minTrendingScore = minTrendingScoreParam ? parseInt(minTrendingScoreParam, 10) : undefined;

    // Parse the date from URL (format: YYYYMMDD)
    const parseDate = (dateString: string) => {
        if (!dateString || dateString.length !== 8) {
            console.error('Invalid date string:', dateString);
            return null;
        }
        const year = parseInt(dateString.substring(0, 4));
        const month = parseInt(dateString.substring(4, 6)) - 1; // 0-based month
        const day = parseInt(dateString.substring(6, 8));

        // Create date in local timezone
        const parsedDate = new Date(year, month, day);

        // Validate the date components match what we expect
        if (parsedDate.getFullYear() !== year ||
            parsedDate.getMonth() !== month ||
            parsedDate.getDate() !== day) {
            console.error('Date components mismatch:', {
                expected: { year, month, day },
                actual: {
                    year: parsedDate.getFullYear(),
                    month: parsedDate.getMonth(),
                    day: parsedDate.getDate()
                }
            });
            return null;
        }

        console.log('Parsing date:', dateString, 'to:', parsedDate.toLocaleString());
        return parsedDate;
    };

    const loadNews = useCallback(async () => {
        if (!date) {
            console.log('[ArchiveSubtype] Missing required date:', { date });
            return;
        }
        
        setLoading(true);
        setError(null);
        
        try {
            console.log('[ArchiveSubtype] Starting to load news with params:', { 
                type,  // Already null if not present
                subtype,
                date,
                minTrendingScore,
                parsedDate: new Date(
                    parseInt(date.substring(0, 4)),
                    parseInt(date.substring(4, 6)) - 1,
                    parseInt(date.substring(6, 8))
                ).toISOString()
            });
            
            const newsData = await fetchArchiveNewsByDate({
                date,
                type,  // Already null if not present
                subtype,
                minTrendingScore: minTrendingScore,
                forceRefresh: true
            });
            
            console.log(`[ArchiveSubtype] Loaded ${newsData.length} news items for ${date}`);
            if (newsData.length === 0) {
                console.log('[ArchiveSubtype] No news items found for the given date');
            } else {
                console.log('[ArchiveSubtype] First news item:', {
                    id: newsData[0].id,
                    title: newsData[0].title,
                    postTime: new Date(newsData[0].postTime * 1000).toISOString()
                });
            }
            
            setNews(newsData);
        } catch (err) {
            console.error('[ArchiveSubtype] Error loading news:', err);
            setError('Failed to load news');
        } finally {
            setLoading(false);
        }
    }, [type, subtype, date, minTrendingScore]);

    useEffect(() => {
        console.log('[ArchiveSubtype] useEffect triggered with:', { type, subtype, date, minTrendingScore });
        setNews([]);
        loadNews();
    }, [type, subtype, date, minTrendingScore, loadNews]);

    const handleNewsClick = (id: string) => {
        const prefix = getUrlLanguagePrefix(language);
        navigate(`${prefix}/trendingnews/${id}`);
    };

    const handleBack = () => {
        navigate(-1); // Navigate to previous page
    };

    const handleArchiveHome = () => {
        const prefix = getUrlLanguagePrefix(language);
        navigate(`${prefix}/archive`);
    };

    // const handleDateSelection = () => {
    //     const prefix = getUrlLanguagePrefix(language);
    //     // Open date selection in a new tab to keep current results visible
    //     let path = '/archive/dates';
        
    //     const queryParams = new URLSearchParams();
        
    //     if (subtype) {
    //         queryParams.set('subtype', subtype);
    //     }
        
    //     if (minTrendingScore !== undefined) {
    //         queryParams.set('minTrendingScore', minTrendingScore.toString());
    //     }
        
    //     if (queryParams.toString()) {
    //         path += `?${queryParams.toString()}`;
    //     }
        
    //     window.open(`${prefix}${path}`, '_blank');
    // };

    const formatDate = (dateString: string) => {
        const date = parseDate(dateString);
        if (!date) {
            console.error('Failed to parse date:', dateString);
            return dateString; // Return original string as fallback
        }
        return date.toLocaleDateString(language, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Get translated type/subtype names
    const getTypeTranslation = (type: string) => {
        return t?.navigations?.header?.[type.toLowerCase()] || type;
    };

    const getSubtypeTranslation = (subtype: string) => {
        return t?.navigations?.header?.[subtype.toLowerCase()] ||
            subtype.charAt(0).toUpperCase() + subtype.slice(1);
    };

    if (!date) {
        return <div className="max-w-4xl mx-auto p-8">Invalid parameters: date is required</div>;
    }

    return (
        <div className="max-w-4xl mx-auto p-8">
            <RobotsNoIndex />
            <div className="flex items-center justify-between mb-8">
                <button
                    onClick={handleBack}
                    className="flex items-center text-blue-600 hover:text-blue-800"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    {t?.navigations?.header?.back || 'Back'}
                </button>
                <button
                    onClick={handleArchiveHome}
                    className="flex items-center text-blue-600 hover:text-blue-800 text-base"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    {t?.navigations?.header?.archiveHome || 'Archive Home'}
                </button>
            </div>

            <div className="mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold mb-2">
                            {subtype ? getSubtypeTranslation(subtype) : 'All News'}
                        </h1>
                        <div className="text-xl text-gray-600">
                            {formatDate(date)}
                        </div>
                        {minTrendingScore !== undefined && (
                            <div className="text-sm text-gray-500 mt-1">
                                {t?.navigations?.header?.trendingLevel || 'Trending Level'}: {minTrendingScore}+
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-8">Loading...</div>
            ) : (
                <div className="space-y-4">
                    {news.length > 0 ? (
                        news
                            .filter(item => item.title && item.title.length >= 3)
                            .map(item => (
                            <div
                                key={item.id}
                                className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                                onClick={() => handleNewsClick(item.id)}
                            >
                                <h3 className="text-lg font-medium text-blue-600 hover:text-blue-800">
                                    {item.title}
                                </h3>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2 text-sm">
                                    <span className="text-gray-600">
                                        {new Date(item.postTime * 1000).toLocaleString()}
                                    </span>
                                    <span className="hidden sm:inline text-gray-400">•</span>
                                    <span className="text-blue-600">
                                        {getTypeTranslation(item.type)}
                                        {item.subtype && (
                                            <>
                                                <span className="text-gray-400 mx-1">-</span>
                                                {getSubtypeTranslation(item.subtype)}
                                            </>
                                        )}
                                    </span>
                                </div>
                            </div>
                        ))
                    ) : (
                        <p className="text-center text-gray-500">
                            {t?.navigations?.header?.noNewsAvailable || 'No news available for this category on this date.'}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default ArchiveSubtype; 